import React, { useState, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Avatar, ClickAwayListener, Popper, Grow, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Iconify } from 'src/components';

/**
 * Generates initials from a name string
 */
const getInitials = (name) => {
  if (!name) return '';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return parts[0][0]?.toUpperCase() || '';
};

/**
 * Generates a consistent color from a string
 */
const stringToColor = (str) => {
  if (!str) return '#637381';
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + (hash * 31);
  }
  const colors = ['#00AB55', '#1890FF', '#7635DC', '#FFC107', '#FF4842', '#04297A', '#7A0C2E'];
  return colors[Math.abs(hash) % colors.length];
};

const CompletedBySelector = ({ users, selectedUserId, onChange, disabled }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  // Build user list with names
  const userList = useMemo(() => (users || []).map((u) => ({
    id: u.id,
    name: u.user ? `${u.user.first_name || ''} ${u.user.last_name || ''}`.trim() : '',
    avatar: u.user?.avatar || null,
  })).filter((u) => u.name), [users]);

  // Find selected user
  const selectedUser = useMemo(() => {
    if (!selectedUserId) return null;
    return userList.find((u) => u.id === selectedUserId) || null;
  }, [selectedUserId, userList]);

  const handleToggle = useCallback(() => {
    if (disabled) return;
    setOpen((prev) => !prev);
  }, [disabled]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSelect = useCallback((userId) => {
    if (disabled) return;
    onChange(userId);
    setOpen(false);
  }, [onChange, disabled]);

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Trigger button */}
      <Box
        ref={anchorRef}
        onClick={handleToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderRadius: '20px',
          border: `1.5px solid ${selectedUser ? theme.palette.primary.main : theme.palette.divider}`,
          backgroundColor: selectedUser ? `${theme.palette.primary.main}08` : theme.palette.background.paper,
          cursor: disabled ? 'default' : 'pointer',
          transition: 'all 0.15s ease',
          '&:hover': disabled ? {} : {
            borderColor: theme.palette.primary.main,
            backgroundColor: `${theme.palette.primary.main}08`,
          },
        }}
      >
        {selectedUser ? (
          <Avatar
            src={selectedUser.avatar}
            sx={{
              width: 24,
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 600,
              bgcolor: stringToColor(selectedUser.name),
            }}
          >
            {getInitials(selectedUser.name)}
          </Avatar>
        ) : (
          <Iconify icon="solar:user-bold" sx={{ width: 18, height: 18, color: theme.palette.text.secondary }} />
        )}
        <Typography sx={{
          fontSize: '0.8rem',
          fontWeight: 500,
          color: selectedUser ? theme.palette.text.primary : theme.palette.text.secondary,
          whiteSpace: 'nowrap',
        }}>
          {selectedUser ? selectedUser.name : 'Select user'}
        </Typography>
        <Iconify
          icon={open ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'}
          sx={{ width: 16, height: 16, color: theme.palette.text.secondary }}
        />
      </Box>

      {/* Dropdown */}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        transition
        sx={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper sx={{
              mt: 0.5,
              borderRadius: '12px',
              boxShadow: theme.shadows[8],
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden',
              minWidth: 200,
            }}>
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  <Typography sx={{
                    px: 2,
                    pt: 1.5,
                    pb: 1,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                  }}>
                    Completed by
                  </Typography>
                  <Box sx={{ maxHeight: 240, overflowY: 'auto', pb: 1 }}>
                    {userList.map((user) => {
                      const isSelected = user.id === selectedUserId;
                      return (
                        <Box
                          key={user.id}
                          onClick={() => handleSelect(user.id)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 2,
                            py: 1,
                            cursor: 'pointer',
                            backgroundColor: isSelected ? `${theme.palette.primary.main}14` : 'transparent',
                            '&:hover': {
                              backgroundColor: isSelected
                                ? `${theme.palette.primary.main}20`
                                : theme.palette.action.hover,
                            },
                          }}
                        >
                          <Avatar
                            src={user.avatar}
                            sx={{
                              width: 32,
                              height: 32,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              bgcolor: stringToColor(user.name),
                            }}
                          >
                            {getInitials(user.name)}
                          </Avatar>
                          <Typography sx={{
                            fontSize: '0.85rem',
                            fontWeight: isSelected ? 600 : 400,
                            color: theme.palette.text.primary,
                            flex: 1,
                          }}>
                            {user.name}
                          </Typography>
                          {isSelected && (
                            <Iconify
                              icon="eva:checkmark-fill"
                              sx={{ width: 18, height: 18, color: theme.palette.primary.main }}
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};

CompletedBySelector.propTypes = {
  users: PropTypes.array,
  selectedUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

CompletedBySelector.defaultProps = {
  users: [],
  selectedUserId: null,
  onChange: () => {},
  disabled: false,
};

export default CompletedBySelector;
